import React from 'react';
// template components
import Col from '@nmx/template/dist/componentLibrary/foundations/Grid/Col';
import Link from '@nmx/template/dist/componentLibrary/components/Link';
import Row from '@nmx/template/dist/componentLibrary/foundations/Grid/Row';
import SectionContainer from '@nmx/template/dist/componentLibrary/components/SectionContainer';
import Typography from '@nmx/template/dist/componentLibrary/foundations/Typography';
import BulletedList from '@nmx/template/dist/componentLibrary/components/BulletedList';
// native components
import ClientServicesPhone4644, { clientServicesPhoneNumberLinkUrl } from '../../../../components/clientServicesPhone4644/clientServicesPhone4644';

export default () => (
  <SectionContainer
    id='anti-fraud-efforts-email-hacking-fraud'
    backgroundVariant='lightA'
    themeType='lightTheme'
    disableBottomPadding>
    <Row align='center'>
      <Col medium={8}>
        <Typography
          variant='h2'
          align='center'>
          Guidance
        </Typography>
        <Typography
          component='h2'
          align='center'>
          Hacked Email Account Concerns and Identity Theft
        </Typography>
        <Typography
          component='h3'>
          Email hacking
        </Typography>
        <Typography>
          <Link
            id='anti-fraud-efforts-email-hacking-fraud-pdf-link'
            variant='primaryDeemphasize'
            linkUrl='/assets/pdf/legal/email-hacking.pdf'
            ariaLabel='Download the email hacking pdf.'
            isInternalPdf>
            Printable version
          </Link>
        </Typography>
        <Typography component='h4'>
          Illegal access to an email account
        </Typography>
        <Typography>
          Email hacking occurs when any unauthorized person accesses another person&#39;s email account. This allows the criminal to read and use any content in the email account&#45;contacts, saved documents, sent items, etc. The criminal can then impersonate the email account owner to obtain funds from existing accounts, open new accounts for money transfers, make unauthorized purchases, and open new credit accounts. Keep in mind that anything in a hacked email account has likely already been copied by criminals, enabling them to continue to impersonate the real owner. When email hacking occurs, emails can&#47;will be intercepted and deleted by the fraudster, and the rightful sender and receiver will no longer see legitimate email traffic. If you believe a fraudster has accessed your email account, we recommend that you take the following actions to protect yourself:
        </Typography>
        <Typography component='h3'>
          Contact your email provider
        </Typography>
        <BulletedList
          bulletedListData={[
            { bulletText: 'Contact your email provider (Gmail, Yahoo, your internet service provider), and follow the recommended steps to reclaim possession of your account.' },
            { bulletText: 'Simply changing the password will not effectively keep the fraudster out.' },
            { bulletText: 'Change your password on any other account where you used the same or similar password as your hacked email account. You should never use the same password across multiple accounts.' },
            { bulletText: 'If you use the hacked email account as a login for other sites, change your passwords and logins for those immediately.' },
            { bulletText: (<>Turn on multi&#45;factor authentication (MFA) for all your accounts that offer MFA.</>) },
          ]} />
        <Typography component='h3'>
          Contact all institutions where your information may be at risk
        </Typography>
        <BulletedList
          bulletedListData={[
            { bulletText: 'Consider other information that has been shared using the hacked email account both currently and in the past (for example, sending tax information to an accountant, etc.).' },
            {
              bulletText: (<>
                <Typography>
                  Alert others (financial institutions, business partners, friends) of the situation:
                </Typography>
                {/* Start secondary unordered list */}
                <BulletedList
                  bulletedListData={[
                    { bulletText: 'Ask them to stop communicating via the hacked email account and remove it from their records.' },
                    { bulletText: 'Consider creating a new email account using MFA and a unique password of at least 14 characters.' },
                    { bulletText: 'Provide them with your new email address.' },
                    { bulletText: (<>Ask your financial institutions to &#34;flag&#34; your account for fraudulent activity.</>) },
                  ]}
                />
                {/* End secondary unordered list */}
              </>),
              useRawImportedBulletContent: true,
            },
            { bulletText: 'Watch all financial accounts closely.' },
          ]} />
        <Typography component='h3'>
          Monitor your credit
        </Typography>
        <BulletedList
          bulletedListData={[
            { bulletText: 'Report attempted identity theft to credit bureaus.' },
            {
              bulletText: (<>
                <Typography>
                  Obtain a free copy of your credit report:
                </Typography>
                {/* Start secondary unordered list */}
                <BulletedList
                  bulletedListData={[
                    {
                      bulletText: (<>
                        Online at <Link
                          id='anti-fraud-efforts-annual-credit-report-link'
                          variant='primaryDeemphasize'
                          linkUrl='https://www.annualcreditreport.com/index.action'
                          isOutgoingLink
                          ariaLabel='Obtain a free copy of your credit report at annual credit report dot com'>
                          www.annualcreditreport.com</Link>,
                      </>),
                    },
                    {
                      bulletText: (<>
                        By calling toll&#45;free <Link
                          id='anti-fraud-efforts-toll-free-credit-report-phone-link'
                          variant='primaryDeemphasize'
                          linkUrl='tel:8773228228'
                          ariaLabel='Call the toll free credit report line'>
                          1&#45;877&#45;322&#45;8228</Link>, or
                      </>),
                    },
                    {
                      bulletText: (<>
                        By mailing an Annual Credit Report Request Form (available at <Link
                          id='anti-fraud-efforts-annual-credit-report-link-2'
                          variant='primaryDeemphasize'
                          linkUrl='https://www.annualcreditreport.com/index.action'
                          isOutgoingLink
                          ariaLabel='Get the Annual Credit Report Request Form.'>
                          www.annualcreditreport.com</Link>) to: Annual Credit Report Request Service, P.O. Box 105281, Atlanta, GA, 30348&#8211;5281.
                      </>),
                    },
                  ]}
                />
                {/* End secondary unordered list */}
              </>),
              useRawImportedBulletContent: true,
            },
            {
              bulletText: (<>Consider using credit freezes or fraud alerts, free services that make it more difficult for criminals to secure credit using your identity. See security freezes and fraud alerts at <Link
                id='anti-fraud-efforts-annual-credit-report-link-3'
                variant='primaryDeemphasize'
                linkUrl='https://www.annualcreditreport.com/index.action'
                isOutgoingLink
                ariaLabel='Annual credit report dot com.'>
                www.annualcreditreport.com</Link>.</>),
            },
          ]} />
        <Typography component='h3'>
          Educate yourself
        </Typography>
        <BulletedList
          bulletedListData={[
            {
              bulletText: (<>
                For tips and other helpful information, contact the Federal Trade Commission (FTC): <Link
                  id='anti-fraud-efforts-toll-free-fraud-detection-link'
                  variant='primaryDeemphasize'
                  linkUrl='tel:8774384338'
                  ariaLabel='Call the Federal Trade Commission toll free line.'>
                  1&#8211;877&#8211;ID&#8211;THEFT</Link> or <Link
                  id='anti-fraud-efforts-ftc-link'
                  variant='primaryDeemphasize'
                  linkUrl='https://www.consumer.ftc.gov/features/feature-0014-identity-theft'
                  isOutgoingLink>
                    www.ftc.gov&#47;bcp&#47;edu&#47;microsites&#47;idtheft&#47;</Link>
              </>),
            },
            { bulletText: 'Remember: never use the same password on multiple accounts. Password reuse is a key cause of email hacking.' },
            { bulletText: 'Tip: You may find a password manager helpful in securely managing your passwords. Research reputable password manager applications or contact your computer professional.' },
          ]} />
        <Typography component='h4'>
          Additional Information
        </Typography>
        <Typography>
          <Link
            id='anti-fraud-efforts-ftc-info-hacked-email-link'
            variant='primaryDeemphasize'
            linkUrl='https://www.consumer.ftc.gov/articles/how-recover-your-hacked-email-or-social-media-account#what%20to%20do%20if'
            isOutgoingLink>
            FTC Information on Hacked Email
          </Link>
        </Typography>
        <Typography>
          <Link
            id='anti-fraud-efforts-ftc-free-credit-reports-link'
            variant='primaryDeemphasize'
            linkUrl='https://www.consumer.ftc.gov/articles/free-credit-reports'
            isOutgoingLink>
            FTC Information on Credit Reports
          </Link>
        </Typography>
        <Typography>
          <Link
            id='anti-fraud-efforts-annual-credit-report-link-4'
            variant='primaryDeemphasize'
            linkUrl='https://www.annualcreditreport.com/index.action'
            isOutgoingLink
            ariaLabel='Go to annual credit report dot com.'>
            Annual Credit Report
          </Link>
        </Typography>
        <Typography>
          <Link
            id='anti-fraud-efforts-security-and-privacy-link'
            variant='primaryDeemphasize'
            linkUrl='/security-and-privacy/'>
            Information Protection at northwesternmutual.com
          </Link>
        </Typography>
        <Typography component='h3'>
          Protect your Northwestern Mutual policies and accounts
        </Typography>
        <Typography>
          Be prepared to provide your account&#47;policy number when requesting the following:
        </Typography>

        <BulletedList
          bulletedListData={[
            {
              bulletText: (<>
                <Typography>
                  Life, Disability Income and Annuity&#8212;You can request a password to be used to verify your identity when you call the home office.
                </Typography>
                {/* Start secondary unordered list */}
                <BulletedList
                  bulletedListData={[
                    {
                      bulletText: (<>
                        For Life and Disability Income Policies, call <Link
                          id='anti-fraud-efforts-income-policies-phone-link'
                          variant='primaryDeemphasize'
                          linkUrl='tel:8003888123'>
                          1&#45;800&#45;388&#45;8123</Link>.
                      </>),
                    },
                    {
                      bulletText: (<>
                        For Annuities, call <Link
                          id='anti-fraud-efforts-annuities-phone-link'
                          variant='primaryDeemphasize'
                          linkUrl='tel:8884552232'>
                          1&#45;888&#45;455&#45;2232</Link>.
                      </>),
                    },
                  ]}
                />
                {/* End secondary unordered list */}
              </>),
              useRawImportedBulletContent: true,
            },
            {
              bulletText: (<>
                Long Term Care&#8212;You can request additional authenticators be added to verify your identity when calling about your policy by calling <Link
                  id='anti-fraud-efforts-authenticators-phone-link'
                  variant='primaryDeemphasize'
                  linkUrl='tel:8007489493'>
                  1&#45;800&#45;748&#45;9493</Link>.
              </>),
            },
            {
              bulletText: (<>
                <Typography>
                  Investment Accounts&#8212;You can request a restriction be added to your account.
                </Typography>
                {/* Start secondary unordered list */}
                <BulletedList
                  bulletedListData={[
                    {
                      bulletText: (<>
                        For Northwestern Mutual Investment Services and Northwestern Mutual Wealth Management Company, including Trust and Private Client Services, call{' '}
                        <Link
                          id='anti-fraud-efforts-investment-services-phone-link'
                          variant='primaryDeemphasize'
                          linkUrl={clientServicesPhoneNumberLinkUrl}>
                          <ClientServicesPhone4644 />
                        </Link>.
                      </>),
                    },
                  ]}
                />
                {/* End secondary unordered list */}
              </>),
              useRawImportedBulletContent: true,
            },
          ]} />
      </Col>
    </Row>
  </SectionContainer>
);
